import type { Occurrence, Recurrence } from './recurrence-api';

/**
 * Classifying an expected period. Pure, because the whole value of this feature rests on getting
 * ONE boolean right and a DOM test is the wrong place to prove arithmetic.
 *
 * ⭐⭐ THREE STATES, NOT TWO:
 *   · `missed`    — the day has passed and no ticket was ever created. THE POINT OF THE FEATURE.
 *   · `done`      — a ticket exists for that period.
 *   · `upcoming`  — the day has not arrived; nothing is wrong yet.
 *
 * 🔴 `upcoming` exists so that "no ticket yet" for TOMORROW is not counted as a failure. Without the
 * distinction the missed count would include every future period the generator has written ahead, and
 * an alarm that always fires is as useless as one that never does.
 */
export type OccurrenceState = 'missed' | 'done' | 'upcoming';

/**
 * 🔴 DATE COMPARISON IS STRING COMPARISON HERE, DELIBERATELY.
 *
 * `expected_on` is a Postgres DATE and the API sends it as `YYYY-MM-DD` text. ⛔ `new Date('2026-08-12')`
 * parses as UTC midnight, which in Asia/Bangkok is 07:00 on the 12th — and `new Date()` on a machine
 * in Bangkok compared against it puts the boundary in the wrong place for seven hours a day. Two
 * ISO date strings compare correctly with `<`, in every timezone, with no parsing at all.
 *
 * ⚠️ `today` is passed IN rather than read from the clock, so "is this missed?" is a pure function
 * and a test can ask about a specific day instead of about whenever it happens to run.
 */
export function todayIso(now: Date = new Date()): string {
  // Local calendar day — the reader's "today", which is what "overdue" means to a person.
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function occurrenceState(occurrence: Occurrence, today: string): OccurrenceState {
  if (occurrence.ticket_id !== null && occurrence.ticket_id !== undefined) return 'done';
  // ⭐ STRICTLY BEFORE today. A period expected TODAY with no ticket yet is not a failure — the day
  // is not over. Counting it would fire an alarm every single morning, and an alarm that always
  // fires is one nobody reads.
  return occurrence.expected_on < today ? 'missed' : 'upcoming';
}

export interface OccurrenceRow {
  occurrence: Occurrence;
  recurrence: Recurrence | null;
  state: OccurrenceState;
}

/**
 * ⚠️ `recurrence` may be null when the templates list and the occurrences list disagree — e.g. the
 * occurrences were fetched unscoped and the templates for one project. The row is KEPT and rendered
 * with its raw `recurrence_id`. ⛔ Dropping it would hide a missed period because of a join, which is
 * the failure this feature exists to prevent, arriving by a side door.
 */
export function buildRows(
  occurrences: readonly Occurrence[],
  recurrences: readonly Recurrence[],
  today: string,
): OccurrenceRow[] {
  const index = new Map(recurrences.map((entry) => [String(entry.id), entry]));
  return occurrences.map((occurrence) => ({
    occurrence,
    recurrence: index.get(String(occurrence.recurrence_id)) ?? null,
    state: occurrenceState(occurrence, today),
  }));
}

/**
 * ⭐⭐ MISSED FIRST, MOST OVERDUE FIRST. Not chronological.
 *
 * A plain date sort buries a 14-day-old miss under two weeks of completed rows — which is precisely
 * how a 14-day gap went unnoticed until a human found it. The ordering is part of the requirement,
 * not a preference.
 */
const STATE_RANK: Record<OccurrenceState, number> = { missed: 0, upcoming: 1, done: 2 };

export function sortForDisplay(rows: readonly OccurrenceRow[]): OccurrenceRow[] {
  return [...rows].sort((a, b) => {
    const byState = STATE_RANK[a.state] - STATE_RANK[b.state];
    if (byState !== 0) return byState;
    // Missed: oldest first (the longest-standing failure leads). Everything else: soonest first.
    if (a.state === 'missed') return a.occurrence.expected_on.localeCompare(b.occurrence.expected_on);
    return a.occurrence.expected_on.localeCompare(b.occurrence.expected_on);
  });
}

export interface OccurrenceTally {
  missed: number;
  done: number;
  upcoming: number;
}

export function tally(rows: readonly OccurrenceRow[]): OccurrenceTally {
  return rows.reduce<OccurrenceTally>(
    (totals, row) => ({ ...totals, [row.state]: totals[row.state] + 1 }),
    { missed: 0, done: 0, upcoming: 0 },
  );
}

/**
 * How many whole days late a missed period is — the number that makes "14 days" visible as a
 * quantity rather than as a date somebody has to subtract in their head.
 *
 * ⭐ Computed from the two ISO strings via `Date.UTC`, so it is a count of calendar days and never
 * off by one because of a DST change or a timezone offset.
 *
 * 🔴 USED ONLY FOR THE PER-TEMPLATE OCCURRENCE LIST, where the API sends no such figure.
 * ⛔ The global missed view uses the SERVER's `days_late`, which Postgres computes against
 * `current_date`. Two ways of getting the same number is two chances to disagree, and they would
 * disagree for several hours a day whenever the reader's timezone is ahead of the database's — the
 * row would be listed as missed by the server while this function called it 0 days late.
 */
export function daysLate(expectedOn: string, today: string): number {
  const toUtc = (iso: string) => {
    const [year, month, day] = iso.split('-').map(Number);
    return Date.UTC(year ?? 1970, (month ?? 1) - 1, day ?? 1);
  };
  const diff = toUtc(today) - toUtc(expectedOn);
  return Math.max(0, Math.round(diff / 86_400_000));
}

/**
 * ⭐⭐ THE LONGEST UNBROKEN RUN OF MISSED PERIODS FOR ONE TEMPLATE — "this duty has been silent for
 * 14 days", which is the sentence somebody needed and nobody had.
 *
 * ⛔ Not the same as the missed COUNT: three misses scattered across a quarter is a different fact
 * from three consecutive weeks of silence, and only the second means the duty has stopped.
 */
export function longestSilence(rows: readonly OccurrenceRow[]): number {
  const byDate = [...rows].sort((a, b) =>
    a.occurrence.expected_on.localeCompare(b.occurrence.expected_on),
  );
  let best = 0;
  let run = 0;
  for (const row of byDate) {
    if (row.state === 'missed') {
      run += 1;
      best = Math.max(best, run);
    } else if (row.state === 'done') {
      run = 0;
    }
    // `upcoming` neither breaks nor extends a run — it has not happened yet.
  }
  return best;
}

export function cadenceLabel(cadence: string, intervalN: number): string {
  if (intervalN <= 1) return cadence;
  const noun = cadence.replace(/ly$/, '');
  return `every ${intervalN} ${noun === 'dai' ? 'days' : `${noun}s`}`;
}
